/* eslint-disable max-len */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import produce from 'immer';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import logger from '../common/logger';
import { RETRY_COUNT, LEARN_LIVE_ENROLLMENT_HOST, LEARN_LIVE_COACHING_REQUEST_SUBMIT_VERSION } from '../common/constants';
import { getEnvironmentConstant } from '../common/utilities';
import { getStaticContent } from './universalDataFetchers';

axiosRetry(axios, { retries: RETRY_COUNT });

function adaptCoachingRequestModelToServiceModel(summary) {
  const coachingSubType = summary.coachingType.product.length > 0 ? summary.coachingType.product : new Array(summary.coachingType.topic);

  const returnValue = {
    CustomerId: summary.customerId,
    CoachingType: { id: summary?.coachingType?.type.id },
    CoachingSubTypes: coachingSubType,
    Objective: summary.objectives,
    CompanyName: summary.companyName,
    SubscriptionTypeId: summary.subscriptionTypeId || 1,
  };

  if (summary.availability?.length > 0) {
    returnValue.DateTimeAvailabilities = summary.availability;
  }
  return returnValue;
}
const getLearnLiveServiceHost = ({ env }) => (`${getEnvironmentConstant({
  env,
  key: LEARN_LIVE_ENROLLMENT_HOST,
})}`);

export const postCoachingRequest = createAsyncThunk(
  'postCoachingRequest',
  ({
    oa2Token,
    env,
    data,
  },
  { rejectWithValue }) => {
    const host = `${getLearnLiveServiceHost({ env })}/coachingrequest/${getEnvironmentConstant({
      env,
      key: LEARN_LIVE_COACHING_REQUEST_SUBMIT_VERSION,
    })}`;
    const headers = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `${oa2Token}`,
      },
    };
    const dataToSend = adaptCoachingRequestModelToServiceModel(data);
    return axios.post(host, dataToSend, headers).catch((err) => rejectWithValue(err));
  },
);

export const postCoachingReferral = createAsyncThunk(
  'postCoachingReferral',
  ({
    oa2Token,
    env,
    data,
  },
  { rejectWithValue }) => {
    const host = `${getLearnLiveServiceHost({ env })}/coachingrequest/referral/v2`;
    const headers = {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `${oa2Token}`,
      },
    };
    const dataToSend = adaptCoachingRequestModelToServiceModel(data);
    return axios.post(host, dataToSend, headers).catch((err) => rejectWithValue(err));
  },
);

export const fetchIdentityData = createAsyncThunk(
  'fetchIdentityData',
  ({ env, token, customerEmail }, { rejectWithValue }) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };
    const identityRequestUrl = `${getLearnLiveServiceHost({ env })}/coachingrequest/${customerEmail ? 'customer' : 'submitter'}/v1`;
    if (customerEmail) {
      return axios
        .post(
          identityRequestUrl,
          { email: customerEmail },
          {
            headers: {
              ...headers,
            },
          },
        )
        .catch((err) => rejectWithValue(err));
    }
    return axios
      .get(identityRequestUrl, {
        headers: {
          ...headers,
        },
      })
      .catch((err) => rejectWithValue(err));
  },
);

export const fetchCoachingTypeData = createAsyncThunk(
  'fetchCoachingTypeData',
  ({ env, token, subscriptionTypeId }, { rejectWithValue }) => {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: token,
    };
    const coachingTypeUrl = `${getLearnLiveServiceHost({ env })}/coachingrequest/scope/v1${subscriptionTypeId ? `?subscriptionTypeId=${subscriptionTypeId}` : ''}`;
    try {
      return axios
        .get(coachingTypeUrl, {
          headers: {
            ...headers,
          },
        });
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const fetchCoachingRequestEditorialData = createAsyncThunk(
  'fetchCoachingRequestEditorialData',
  ({
    slugs, env, isPreview, isSSR,
  }, { rejectWithValue }) => getStaticContent({
    slugs, env, isPreview, isSSR,
  }).then((res) => (res)).catch((err) => (rejectWithValue(err))),
);

export const coachingRequestSlice = createSlice({
  name: 'coachingRequestSlice',
  initialState: {
    identityData: {
      data: {},
      hasFetched: false,
      isLoaderVisible: true,
    },
    referrer: {
      data: null,
    },
    coachingTypeData: {
      data: [],
      hasFetched: false,
      isLoaderVisible: true,
    },
    requestSummary: {
      customerId: null,
      companyName: '',
      coachingType: {
        type: {},
        product: [],
        topic: {},
      },
      objectives: '',
      availability: [],
      subscriptionTypeId: null,
    },
    isTermsAccepted: false,
  },
  reducers: {
    setCoachingType: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.requestSummary.coachingType = action?.payload;
      });
      return newState;
    },
    setCompanyName: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.requestSummary.companyName = action?.payload;
      });
      return newState;
    },
    setCoachingSubType: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.requestSummary.coachingSubType = action.payload;
      });
      return newState;
    },
    setAvailability: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.requestSummary.availability = action?.payload;
      });
      return newState;
    },
    setObjectives: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.requestSummary.objectives = action?.payload;
      });
      return newState;
    },
    setTermsAccepted: (state, action) => {
      const newState = produce(state, (draftState) => {
        draftState.isTermsAccepted = action?.payload;
      });
      return newState;
    },
    setReferrer: (state, action) => {
      const newState = produce(state, (draftState) => { draftState.referrer.data = action?.payload; });
      return newState;
    },
    setSubscriptionTypeId: (state, action) => {
      const newState = produce(state, (draftState) => { draftState.requestSummary.subscriptionTypeId = action?.payload; });
      return newState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchIdentityData.pending, (state) => ({
      ...state,
      identityData: { isLoaderVisible: true },
    }));
    builder.addCase(fetchIdentityData.fulfilled, (state, action) => {
      const data = action?.payload || {};
      if (!data.error) {
        const newState = produce(state, (draftState) => {
          if (draftState?.identityData?.data?.userId === data?.data?.userId) {
            // Merge
            draftState.identityData.data = {
              ...draftState.identityData.data,
              ...data.data,
            };
          } else {
            // Overwrite
            draftState.identityData.data = data.data;
            draftState.requestSummary.customerId = data.data.userId;
          }
          if (data.data.isPremiumSubscriber || data.data.isAdministrator) {
            draftState.requestSummary.subscriptionTypeId = 1;
          }
          if (data.data.isEnterpriseAdmin || data.data.isEnterpriseSubscriber) {
            draftState.requestSummary.subscriptionTypeId = 2;
          }
          draftState.identityData.isLoaderVisible = false;
          draftState.identityData.hasFetched = true;
        });

        return newState;
      }

      return state;
    });
    builder.addCase(fetchIdentityData.rejected, (state, action) => {
      logger.error('Error in fetchIdentityData: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.identityData.hasError = true;
        draftState.identityData.isLoaderVisible = false;
        draftState.identityData.hasFetched = true;
      });

      return newState;
    });
    builder.addCase(postCoachingRequest.pending, (state) => {
      const newState = produce(state, (draftState) => {
        delete draftState.enrollmentError;
        draftState.enrolling = true;
      });

      return newState;
    });
    builder.addCase(postCoachingRequest.fulfilled, (state, action) => {
      const data = action?.payload || {};
      if (!data.error) {
        const newState = produce(state, (draftState) => {
          draftState.data = data.data?.classData;
          draftState.enrolling = false;
        });

        return newState;
      }

      return state;
    });
    builder.addCase(postCoachingRequest.rejected, (state, action) => {
      logger.error('Error in postCoachingRequest: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.enrollmentError = action?.payload?.response?.status === 401 ? 'Invalid authorization code.' : 'Authorization failure. Please try again later. If failure persists, please contact your administrator.';
        draftState.enrolling = false;
      });

      return newState;
    });
    builder.addCase(fetchCoachingTypeData.pending, (state) => ({
      ...state,
      coachingTypeData: { isLoaderVisible: true },
    }));
    builder.addCase(fetchCoachingTypeData.fulfilled, (state, action) => {
      const data = action?.payload || {};
      if (!data.error && data?.data?.CoachingTypes) {
        let coachingType = [];
        const newState = produce(state, (draftState) => {
          // Merge
          Object.values(data?.data?.CoachingTypes)
            .forEach((item) => {
              coachingType = [...coachingType, {
                children: item.label, value: item.key, id: item.id, coachingSubtype: item.coachingSubTypes,
              }];
            });
          draftState.coachingTypeData.data = coachingType;
          draftState.coachingTypeData.isLoaderVisible = false;
          draftState.coachingTypeData.hasFetched = true;
        });
        return newState;
      }
      return state;
    });
    builder.addCase(fetchCoachingTypeData.rejected, (state, action) => {
      logger.error('Error in fetchIdentityData: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.identityData.hasError = true;
        draftState.identityData.isLoaderVisible = false;
      });

      return newState;
    });
    builder.addCase(fetchCoachingRequestEditorialData.pending, (state) => ({
      ...state,
    }));
    builder.addCase(fetchCoachingRequestEditorialData.fulfilled, (state, action) => {
      const data = action?.payload;
      if (!data?.error) {
        const newState = produce(state, (draftState) => {
          draftState.editorial = data;
        });
        return newState;
      }

      return state;
    });
    builder.addCase(fetchCoachingRequestEditorialData.rejected, (state, action) => {
      logger.error('Error in fetchCoachingRequestEditorialData: ', action.payload);
      const newState = produce(state, (draftState) => {
        draftState.hasEditorialError = true;
      });
      return newState;
    });
  },
});

// eslint-disable-next-line no-empty-pattern
export const {
  setCoachingType, setCompanyName, setObjectives, setTermsAccepted, setAvailability, setReferrer, setSubscriptionTypeId,
} = coachingRequestSlice.actions;

export const getIdentityData = (state) => state.coachingRequestData.identityData.data;
export const getReferrerData = (state) => state.coachingRequestData.referrer;
export const hasIdentityDataFetched = (state) => state.coachingRequestData.identityData.hasFetched;
export const isLoaderVisible = (state) => state.coachingRequestData.isLoaderVisible;
export const IsLoggedInButNotEligible = (state) => {
  const data = state.coachingRequestData?.identityData?.data;
  if (data) {
    if (data.isAdministrator === false && data.isAutodeskEmployee === false && data.isPremiumSubscriber === false && data.isEnterpriseAdmin === false && data.isEnterpriseSubscriber === false) {
      return true;
    }
  }
  return false;
};

export const isAccountLevelButNotAdmin = (state) => {
  const data = state.coachingRequestData?.identityData?.data;
  if (data) {
    if (data.isPremiumSubscriber === true && data.isAdministrator === false) {
      return true;
    }
    if (data.isEnterpriseAdmin === false && data.isEnterpriseSubscriber === true) {
      return true;
    }
  }
  return false;
};
export const isSubscriptionTypeEnterprise = (state) => (state?.coachingRequestData?.identityData?.data?.isEnterpriseAdmin || state?.coachingRequestData?.identityData?.data?.isEnterpriseSubscriber) || false;
export const isAllowedToSubmitRequest = (state) => (state?.coachingRequestData?.identityData?.data?.isEnterpriseAdmin || state?.coachingRequestData?.identityData?.data?.isAdministrator) || false;
export const getSubscriptionTypeLabel = (state) => {
  if (isSubscriptionTypeEnterprise(state)) {
    return 'Enterprise';
  }
  if (state?.coachingRequestData?.identityData?.data?.isPremiumSubscriber) {
    return 'Premium';
  }
  if (state?.coachingRequestData?.identityData?.hasFetched) {
    return 'not eligible';
  }
  return undefined;
};
export const isPremiumCustomer = (state) => (state.coachingRequestData?.identityData?.data?.isPremiumSubscriber && state.coachingRequestData?.identityData?.data?.isAdministrator) || false;
export const requestSummary = (state) => state.coachingRequestData?.requestSummary;
export const getCoachingType = (state) => state.coachingRequestData?.coachingTypeData?.data;
export const getEditorialData = (state) => state.coachingRequestData?.editorial;
export default coachingRequestSlice.reducer;
